export const SUBSCRIPTION_PRODUCT_BIOFLOW = "sal-bioflow";

export type BioFlowBillingCycle = "monthly" | "yearly";

export type BioFlowPaymentMethod = "card" | "bank" | "purchase";

export interface BioFlowPlanQuotation {
  id: number;
  duration: number;
  price: number;
  currency: string;
  region: string;
  product: string;
  billingCycle?: BioFlowBillingCycle | null;
  billingModel?: string;
  isOperational?: boolean;
}

export interface BioFlowSubscriptionStatus {
  active: boolean;
  endDate?: string | null;
  type?: string | null;
  product: string;
  renewalWindowDays?: number;
  daysUntilExpiry?: number | null;
  renewalAllowed?: boolean;
}

export interface CreateBioFlowOrderRequest {
  quotationId: number;
  paymentMethod: BioFlowPaymentMethod;
  product: typeof SUBSCRIPTION_PRODUCT_BIOFLOW;
  proveUrl?: string;
  gateway?: "tap" | "myfatoorah";
}

export interface BioFlowOrderResponse {
  id: number;
  status: string;
  paymentMethod: string;
  price: number;
  product: string;
  url?: string;
  date?: string;
}

/** Display prices — server quotation is source of truth for checkout */
export const BIOFLOW_DISPLAY_PRICES = {
  monthly: 15,
  yearly: 165,
  currency: "USD",
} as const;

export const BIOFLOW_FEATURES = [
  {
    id: "bioflow.feature.fullAccess",
    defaultMessage: "Full SALBioFlow application access",
  },
  {
    id: "bioflow.feature.biowellScans",
    defaultMessage: "BioFlow scan integration",
  },
  {
    id: "bioflow.feature.healthInsights",
    defaultMessage: "Body and energy insights",
  },
] as const;

export const BIOFLOW_ONBOARDING_VERIFIED_KEY = "bioflowOnboardingVerified";

export type BioFlowSyncStatus = "synced" | "pending" | "failed";

export interface BioFlowAccountStatus {
  userId: number;
  exists: boolean;
  biowellLogin?: string;
  registered: boolean;
  partnerRegistered?: boolean;
  registeredAt?: string | null;
  syncStatus?: BioFlowSyncStatus | null;
  lastSyncError?: string | null;
  lastSyncAttemptAt?: string | null;
  setupComplete: boolean;
  onboardingCompletedAt?: string | null;
  onboardingComplete: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Direct BioWell requirement: at least 6 Latin letters or digits. */
export function isValidBioFlowPassword(password: string): boolean {
  return typeof password === "string" && /^[A-Za-z0-9]{6,}$/.test(password);
}

export interface BioFlowHandoffCredentials {
  password: string;
}

export interface BioFlowHandoffStartRequest {
  password?: string;
}

export interface RegisterBioFlowCredentialsRequest {
  password: string;
}

export interface BioFlowHandoffStartResponse {
  redirectUrl: string;
}

export interface BioFlowVerifyCallbackResponse {
  valid: true;
  biowellLogin: string;
  sal125UserId: number;
  onboardingComplete: boolean;
  intent?: string;
}

export type BioFlowHandoffErrorCode =
  | "expired_token"
  | "invalid_token"
  | "account_not_found"
  | "onboarding_incomplete";
